import { useCallback, useMemo, useState } from "react";
import { useApolloClient, useLazyQuery } from "@apollo/client";

import {
  ACCOUNT_KEY,
  AuthContext,
  CONTEXT_KEY,
  LOGGED_OUT_STATE,
  ORGANIZATION_KEY,
  type AuthAccount,
  type AuthOrganization,
  type AuthState,
  type AuthUser,
  type ImpersonationContext,
} from "./AuthContext";
import { GET_ORGANIZATIONS } from "./AuthApi";
import {
  markLogoutIntent,
  type LogoutIntent,
  persistSsoEnforcementForHomeOrg,
  recordSuccessfulAuthentication,
} from "./ssoSession";
import useCognitoAuthWithLogs from "./useCognitoAuthWithLogs";
import GeneralAppProvider from "../General/GeneralAppProvider";
import AppRouter from "../AppRouter/AppRouter";
import { useAppStyling } from "../WhiteLabel/useAppStyling";
import useLogger from "../Logger/useLogger";

interface GetOrganizationsData {
  organizations: AuthOrganization[];
}

export const AuthProvider: React.FC = () => {
  const [authState, setAuthState] = useState<AuthState>({
    ...LOGGED_OUT_STATE,
  });
  const [organizations, setOrganizations] = useState<
    AuthOrganization[] | undefined
  >(undefined);

  const client = useApolloClient();
  const { signOut } = useCognitoAuthWithLogs();
  const { appProps } = useAppStyling();
  const { logError } = useLogger();
  const updateAuthState = useCallback(
    (state: Partial<AuthState>) =>
      setAuthState((currentState) => ({ ...currentState, ...state })),
    [setAuthState]
  );
  const logout = useCallback(
    (options?: LogoutIntent) => {
      // Tag a DELIBERATE logout first, so a throw in the Apollo/Amplify
      // cleanup below can never skip it. An error-driven logout (401/403)
      // passes { userInitiated: false } and is left untagged, so an expired
      // session can still resume SSO.
      markLogoutIntent(options);
      const logoutUrl = `/logout${appProps?.mspQueryString ?? ""}`;
      const redirect = () => {
        window.location.href = logoutUrl;
      };
      try {
        client.stop();
        // clearStore is preferred over resetStore: resetStore re-fetches all active
        // queries, which we don't want here since we're about to leave the page.
        client.clearStore().finally(() => {
          // Race signOut against a 3-second timeout so a hung Amplify signOut
          // (Signout timeout fail, WAK-33496) never leaves the user on a broken page.
          let timeoutId: ReturnType<typeof setTimeout>;
          const signOutTimeout = new Promise<never>((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error("Signout timeout")),
              3000
            );
          });
          Promise.race([signOut(), signOutTimeout])
            .catch((err) => logError(err, "logout error"))
            .finally(() => {
              clearTimeout(timeoutId);
              redirect();
            });
        });
      } catch {
        redirect();
      }
    },
    [client, signOut, appProps, logError]
  );

  const [getOrganizations] =
    useLazyQuery<GetOrganizationsData>(GET_ORGANIZATIONS);

  const chooseOrganizationAndAccount = useCallback(
    (organization: AuthOrganization, account: AuthAccount) => {
      window.localStorage.setItem(ORGANIZATION_KEY, organization.slug);
      window.localStorage.setItem(ACCOUNT_KEY, account.id);

      updateAuthState({ organization, account, authenticated: true });
    },
    [updateAuthState]
  );

  const loadOrganizations = useCallback(
    async (impersonation: ImpersonationContext) => {
      const { data } = await getOrganizations({
        context: {
          // The `authState` still doesn't contain the impersonation context (CONTEXT_KEY),
          // but we need the ApolloLinks to have it in order to route this query
          // to the CNC endpoint instead of the standard graphql one.
          impersonation,
        },
      });
      const orgs = data?.organizations ?? [];
      setOrganizations(orgs);

      // Every selection site goes through this wrapper so the "SSO enforced"
      // hint is persisted for the user's HOME org only. The impersonation guard
      // lives inside the helper, so the impersonation-match branch below can
      // share it safely.
      const selectOrganizationAndAccount = (
        organization: AuthOrganization,
        account: AuthAccount
      ) => {
        persistSsoEnforcementForHomeOrg(organization, impersonation);
        return chooseOrganizationAndAccount(organization, account);
      };

      if (orgs.length === 1) {
        const organization = orgs[0];
        if (organization.accounts.length === 1) {
          return selectOrganizationAndAccount(
            organization,
            organization.accounts[0]
          );
        }
      }

      if (impersonation?.id) {
        // Iterate through the organizations and find the account
        for (const org of orgs) {
          for (const account of org.accounts) {
            if (account.id === impersonation.id) {
              return selectOrganizationAndAccount(org, account);
            }
          }
        }
      }

      try {
        const selectedOrganization =
          window.localStorage.getItem(ORGANIZATION_KEY);
        const selectedAccount = window.localStorage.getItem(ACCOUNT_KEY);
        const organization = orgs.find(
          (org) => org.slug === selectedOrganization
        );
        if (organization) {
          const account = organization.accounts.find(
            (acc) => acc.id === selectedAccount
          );
          if (account) {
            return selectOrganizationAndAccount(organization, account);
          }
        }
      } catch {}
    },
    [getOrganizations, chooseOrganizationAndAccount]
  );

  const populateAuthentication = useCallback(
    async (cognitoUser: AuthUser, mfaType?: string | null) => {
      let email = cognitoUser?.attributes?.email ?? cognitoUser?.username;
      const hashedId =
        cognitoUser?.signInUserSession?.accessToken?.payload?.sub;
      const idTokenPayload = cognitoUser?.signInUserSession?.idToken?.payload;
      if (idTokenPayload?.email) {
        email = idTokenPayload.email;
      }
      if (idTokenPayload?.identities?.length) {
        email = idTokenPayload.identities[0].userId;
      }

      // Remember the user's OWN IdP name, taken from their own id token —
      // never from the organization object, which during CNC impersonation
      // belongs to the impersonated customer — and lift the IdP-failure and
      // manual-logout vetoes now that they have signed in successfully.
      recordSuccessfulAuthentication(idTokenPayload);

      const domain = email?.substring(email?.lastIndexOf("@") + 1);
      let context: ImpersonationContext = {
        mainDomain: domain,
      };

      try {
        const contextFromLS = JSON.parse(
          window.localStorage.getItem(CONTEXT_KEY) ?? "null"
        ) as ImpersonationContext | null;
        if (contextFromLS?.id) {
          context = {
            ...context, // if there's `mainDomain` in LS, it will be overwritten as expected.
            ...contextFromLS,
          };
        }
      } catch {}

      updateAuthState({
        [CONTEXT_KEY]: context,
        user: cognitoUser,
        username: email,
        hashedUserId: hashedId,
        domain,
        mfaType,
      });

      return loadOrganizations(context);
    },
    [updateAuthState, loadOrganizations]
  );

  const clearOrgAndAccount = useCallback(() => {
    window.localStorage.removeItem(ORGANIZATION_KEY);
    window.localStorage.removeItem(ACCOUNT_KEY);
  }, []);

  const stopImpersonation = useCallback(() => {
    window.localStorage.removeItem(CONTEXT_KEY);
  }, []);
  const isImpersonating = useMemo(
    () => authState.context?.mainDomain !== authState.domain,
    [authState?.context, authState?.domain]
  );

  return (
    <AuthContext.Provider
      value={{
        authState,
        organizations,
        updateAuthState,
        chooseOrganizationAndAccount,
        populateAuthentication,
        clearOrgAndAccount,
        stopImpersonation,
        logout,
        isImpersonating,
      }}
    >
      <GeneralAppProvider>
        <AppRouter />
      </GeneralAppProvider>
    </AuthContext.Provider>
  );
};

export default AuthProvider;
